import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { ApiError } from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

export type ExtendedRequestConfig = AxiosRequestConfig & {
  _retry?: boolean;
  skipAuthRefresh?: boolean;
};

class ApiService {
  private client: AxiosInstance;
  private accessToken: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000,
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        if (this.accessToken) {
          config.headers.Authorization = `Bearer ${this.accessToken}`;
        } else {
          // Fallback: tenta recuperar do localStorage se ainda não reidratou no store
          try {
            const raw = localStorage.getItem('auth-storage');
            if (raw) {
              const parsed = JSON.parse(raw);
              if (parsed?.state?.accessToken) {
                this.accessToken = parsed.state.accessToken;
                config.headers.Authorization = `Bearer ${parsed.state.accessToken}`;
              }
            }
          } catch {
            // ignore localStorage parse errors
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<ApiError>) => {
        const originalRequest = (error.config || {}) as ExtendedRequestConfig;
        const status = error.response?.status;

        if (status === 401 && !originalRequest._retry && !originalRequest.skipAuthRefresh) {
          originalRequest._retry = true;

          try {
            const refreshResponse = await this.client.post<{
              success: boolean;
              data: { accessToken: string };
            }>('/auth/refresh', undefined, { skipAuthRefresh: true } as ExtendedRequestConfig);

            const newToken = refreshResponse.data.data.accessToken;
            this.setAccessToken(newToken);

            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return this.client(originalRequest);
          } catch (refreshError) {
            this.clearAccessToken();
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(this.handleError(error));
      }
    );
  }

  setAccessToken(token: string | null) {
    this.accessToken = token;
  }

  clearAccessToken() {
    this.accessToken = null;
  }

  private handleError(error: AxiosError<ApiError>): ApiError {
    if (error.response?.data) {
      return error.response.data;
    }
    
    return {
      message: error.message || 'Ocorreu um erro inesperado',
      code: 'UNKNOWN_ERROR',
      status: error.response?.status || 500,
    };
  }

  async get<T>(url: string, config?: AxiosRequestConfig) {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: unknown, config?: ExtendedRequestConfig) {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig) {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig) {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig) {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }
}

export const api = new ApiService();

export const setApiAuthToken = (token: string | null) => {
  api.setAccessToken(token);
};

export const clearApiAuthToken = () => {
  api.clearAccessToken();
};

export default api;
